import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { AuthContext } from '@mint-lib/auth';
import { useEvent } from '@mint-lib/hooks';
import { useQueryClient } from '@mint-lib/query-client';
import { AiAssistantSocket, Chat } from '@mint-sdk/api-ai-assistant';
import { useSetAtom } from 'jotai';

import { chatIdAtom } from './atoms/chat.js';
import { createChatSocketClient } from './createChatSocketClient.js';
import { useInternetConnectionRestored } from './helperHooks/useInternetConnectionRestored.jsx';

type SocketContextValue = {
  chatId: string;
  chatSocket: AiAssistantSocket.ChatSocket | null;
  connect: () => void;
  /** Replays the handshake: the server expires an idle session after 5 minutes. */
  authenticate: () => void;
  getConnected: () => boolean;
};

const SocketContext = createContext<SocketContextValue | null>(null);

type SocketProviderProps = {
  chatId: string;
  children: ReactNode;
};

export const SocketProvider = ({ chatId, children }: SocketProviderProps) => {
  const auth = useContext(AuthContext);
  const queryClient = useQueryClient();
  const setChatId = useSetAtom(chatIdAtom);

  useEffect(() => {
    setChatId(chatId);
    return () => {
      setChatId(null);
    };
  }, [chatId, setChatId]);

  /**
   * Built in the memo body, not an effect, because children subscribe from
   * their own effects, which run before the parent's.
   */
  const chatSocket = useMemo(() => {
    if (!chatId) {
      return null;
    }

    return AiAssistantSocket.createChatSocket({
      client: createChatSocketClient(chatId),
    });
  }, [chatId]);

  const authenticate = useEvent(() => {
    if (!chatSocket) {
      return;
    }

    auth.ensureTokensValid().then(() => {
      const token = auth.getAccessToken();

      if (token && chatSocket.getConnected()) {
        chatSocket.authenticate({ authorization: encodeURIComponent(token) });
      }
    });
  });

  useEffect(() => {
    if (!chatSocket) {
      return;
    }

    const unsubscribe = chatSocket.onConnect(authenticate);
    chatSocket.connect();

    return () => {
      unsubscribe();
      chatSocket.disconnect();
    };
  }, [chatSocket, authenticate]);

  const handleConnectionRestored = useCallback(() => {
    const chatHistoryQueryKey = Chat.getHistoryById({
      urlParams: { chatSessionId: chatId },
    }).queryKey;

    queryClient.invalidateQueries({
      queryKey: chatHistoryQueryKey,
    });
  }, [chatId, queryClient]);

  useInternetConnectionRestored(handleConnectionRestored);

  const connect = useCallback(() => {
    chatSocket?.connect();
  }, [chatSocket]);

  const getConnected = useCallback(
    () => chatSocket?.getConnected() ?? false,
    [chatSocket],
  );

  const value = useMemo(
    () => ({
      chatId,
      chatSocket,
      connect,
      authenticate,
      getConnected,
    }),
    [chatId, chatSocket, connect, authenticate, getConnected],
  );

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextValue => {
  const context = useContext(SocketContext);

  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }

  return context;
};
